import { useEffect, useMemo, useRef } from 'react';
import { BusinessKnowledge } from '@weave/schema-gen-ts/dist/schemas/ai-settings/v1/ai_settings.pb';
import dayjs from 'dayjs';
import isURL from 'validator/es/lib/isURL';
import { ScheduleAvailabilityTypes } from '@frontend/api-schedule-availability';
import { useTranslation } from '@frontend/i18n';
import { AvailabilityHoursFormConfig, getAvailabilityHoursFormFields } from '@frontend/schedule-availability-hours';
import {
  useForm,
  FormFieldActionTypes,
  PostalCodeLocale,
  FieldProps,
  FormConfig,
  ValidatorFieldState,
  FormValues,
} from '@frontend/design-system';
import { useAIRBusinessKnowledgeStore } from '../../../../stores/air-settings.store';
import { BusinessHoursMap } from '../../../../types/air-settings.types';
import { DEFAULT_BUSINESS_HOURS, DEFAULT_TIME_RANGE } from '../constants';

const BUSINESS_NAME_MAX_LENGTH = 250;

const DAYS: ScheduleAvailabilityTypes.Weekday[] = [
  ScheduleAvailabilityTypes.Weekday.monday,
  ScheduleAvailabilityTypes.Weekday.tuesday,
  ScheduleAvailabilityTypes.Weekday.wednesday,
  ScheduleAvailabilityTypes.Weekday.thursday,
  ScheduleAvailabilityTypes.Weekday.friday,
  ScheduleAvailabilityTypes.Weekday.saturday,
  ScheduleAvailabilityTypes.Weekday.sunday,
];

// Cast/type helpers for hours form fields
interface DynamicTimeRangeField {
  type: 'timeRange';
  value: [string, string];
  interval?: number;
}

const baseHoursFields = (() => {
  const fields = getAvailabilityHoursFormFields();
  Object.keys(fields).forEach((key) => {
    const fieldKey = key as keyof typeof fields;
    const day = key.split('-')[0] as ScheduleAvailabilityTypes.Weekday;
    if (key.endsWith('-time-range')) {
      const timeRangeField = fields[fieldKey] as unknown as DynamicTimeRangeField;
      timeRangeField.interval = 30;
      timeRangeField.value = DEFAULT_BUSINESS_HOURS[day]
        ? [DEFAULT_BUSINESS_HOURS[day].start, DEFAULT_BUSINESS_HOURS[day].end]
        : ['', ''];
    } else if (key.endsWith('-checkbox')) {
      fields[fieldKey].value = !!DEFAULT_BUSINESS_HOURS[day];
    }
  });
  return fields;
})();

export interface BusinessKnowledgeBaseFields extends FormConfig {
  businessNameSource: { type: 'dropdown'; value: string; required?: boolean };
  businessNameCustomValue: { type: 'text'; value: string; required?: boolean };
  businessAddressSource: { type: 'dropdown'; value: string; required?: boolean };
  addressLine1: { type: 'text'; value: string; required?: boolean };
  addressLine2: { type: 'text'; value: string };
  city: { type: 'text'; value: string; required?: boolean };
  state: { type: 'dropdown'; value: string; required?: boolean };
  postalCode: { type: 'postalCode'; value: string; required?: boolean; locale: PostalCodeLocale };
  businessWebsiteLink: { type: 'text'; value: string; required?: boolean };
  businessWebsiteFaqLink: { type: 'text'; value: string; required?: boolean };
}

export type BusinessKnowledgeFormFields = AvailabilityHoursFormConfig & BusinessKnowledgeBaseFields;

export type BusinessNameSourceFieldProps = FieldProps<BusinessKnowledgeFormFields, 'businessNameSource'>;
export type BusinessNameCustomValueFieldProps = FieldProps<BusinessKnowledgeFormFields, 'businessNameCustomValue'>;
export type BusinessAddressSourceFieldProps = FieldProps<BusinessKnowledgeFormFields, 'businessAddressSource'>;
export type AddressLine1FieldProps = FieldProps<BusinessKnowledgeFormFields, 'addressLine1'>;
export type AddressLine2FieldProps = FieldProps<BusinessKnowledgeFormFields, 'addressLine2'>;
export type CityFieldProps = FieldProps<BusinessKnowledgeFormFields, 'city'>;
export type StateFieldProps = FieldProps<BusinessKnowledgeFormFields, 'state'>;
export type PostalCodeFieldProps = FieldProps<BusinessKnowledgeFormFields, 'postalCode'>;
export type BusinessWebsiteFieldProps = FieldProps<BusinessKnowledgeFormFields, 'businessWebsiteLink'>;
export type BusinessFaqFieldProps = FieldProps<BusinessKnowledgeFormFields, 'businessWebsiteFaqLink'>;

interface UseBusinessKnowledgeFormProps {
  locationId: string;
  isManageAll: boolean;
}

// Strongly typed helpers for dynamic form access
export const getHoursCheckboxValue = (values: FormValues<BusinessKnowledgeFormFields>, day: string): boolean => {
  return !!values[`${day}-checkbox` as any];
};

export const getHoursTimeRangeValue = (
  values: FormValues<BusinessKnowledgeFormFields>,
  day: string
): [string, string] => {
  return (values[`${day}-time-range`] as [string, string]) || ['', ''];
};

export const useBusinessKnowledgeForm = ({ locationId, isManageAll }: UseBusinessKnowledgeFormProps) => {
  const { t } = useTranslation('ai-receptionist-settings');
  const { draftSettings, updateDraft, fieldErrors, setFieldError } = useAIRBusinessKnowledgeStore([
    'draftSettings',
    'updateDraft',
    'fieldErrors',
    'setFieldError',
  ]);

  const settingsData = useMemo(() => {
    return draftSettings[locationId] || {};
  }, [draftSettings, locationId]);

  const hasBusinessAddressField = settingsData?.addressOverride !== undefined;
  const hasBusinessWebsiteField = settingsData?.businessWebsiteLink !== undefined;
  const hasBusinessWebsiteFaqField = settingsData?.businessWebsiteFaqLink !== undefined;

  // Use refs for conditional flags to avoid stale closures in useForm's validators
  const hasBusinessAddressFieldRef = useRef(hasBusinessAddressField);
  const hasBusinessWebsiteFieldRef = useRef(hasBusinessWebsiteField);
  const hasBusinessWebsiteFaqFieldRef = useRef(hasBusinessWebsiteFaqField);
  const isManageAllRef = useRef(isManageAll);

  hasBusinessAddressFieldRef.current = hasBusinessAddressField;
  hasBusinessWebsiteFieldRef.current = hasBusinessWebsiteField;
  hasBusinessWebsiteFaqFieldRef.current = hasBusinessWebsiteFaqField;
  isManageAllRef.current = isManageAll;

  const { getFieldProps, values, seedValues } = useForm<BusinessKnowledgeFormFields, keyof BusinessKnowledgeFormFields>(
    {
      fields: {
        // Business Name
        businessNameSource: {
          type: 'dropdown',
          value: settingsData.name?.source,
          required: !isManageAll,
        },
        businessNameCustomValue: {
          type: 'text',
          value: settingsData.name?.value || '',
          required: true,
          validator: (field: ValidatorFieldState<'text'>) => {
            if (field.value.length > BUSINESS_NAME_MAX_LENGTH) {
              return t('Business name must be less than {{maxLength}} characters', {
                maxLength: BUSINESS_NAME_MAX_LENGTH,
              });
            }

            return '';
          },
        },

        // Business Address
        businessAddressSource: {
          type: 'dropdown',
          value: settingsData.addressOverride?.source,
          required: hasBusinessAddressField,
        },
        addressLine1: {
          type: 'text',
          required: true,
        },
        addressLine2: { type: 'text', value: '' },
        city: {
          type: 'text',
          required: true,
        },
        state: {
          type: 'dropdown',
          required: true,
        },
        postalCode: {
          type: 'postalCode',
          locale: PostalCodeLocale.US,
          required: true,
        },

        // Business Website
        businessWebsiteLink: {
          type: 'text',
          required: !isManageAll,
          validator: (field: ValidatorFieldState<'text'>) => {
            if (!isURL(field.value, { require_protocol: false })) {
              return t('Enter valid website link');
            }
            return '';
          },
        },

        // FAQs
        businessWebsiteFaqLink: {
          type: 'text',
          required: !isManageAll,
          validator: (field: ValidatorFieldState<'text'>) => {
            if (!isURL(field.value, { require_protocol: false })) {
              return t('Enter valid faqs link');
            }
            return '';
          },
        },

        // Hours
        ...baseHoursFields,
      } as unknown as BusinessKnowledgeFormFields,
      fieldStateReducer: (state, action) => {
        let finalState = { ...state };
        if (action.type === FormFieldActionTypes.Update && action.payload.name.endsWith('-checkbox')) {
          const day = action.payload.name.split('-')[0];
          const stateKey = `${day}-time-range`;
          const typedState = state as unknown as Record<string, { value: any; displayValue?: any }>;

          if (!action.payload.value) {
            finalState = {
              ...state,
              [stateKey]: { ...typedState[stateKey], value: ['', ''] },
            } as typeof state;
          } else {
            finalState = {
              ...state,
              [stateKey]: {
                ...typedState[stateKey],
                value: [DEFAULT_TIME_RANGE.start, DEFAULT_TIME_RANGE.end],
                displayValue: [
                  dayjs(DEFAULT_TIME_RANGE.start, 'HH:mm').format('h:mma'),
                  dayjs(DEFAULT_TIME_RANGE.end, 'HH:mm').format('h:mma'),
                ],
              },
            } as typeof state;
          }
        }
        return finalState;
      },
    }
  );

  const isInternalUpdate = useRef(false);

  // Sync settingsData changes from store into the form state (External -> Internal)
  useEffect(() => {
    if (!isInternalUpdate.current) {
      const newFormValues: Record<string, any> = {
        businessNameSource: settingsData.name?.source || '',
        businessNameCustomValue: settingsData.name?.value || '',
        businessAddressSource: settingsData.addressOverride?.source || '',
        addressLine1: settingsData.addressOverride?.addressLine1 || '',
        addressLine2: settingsData.addressOverride?.addressLine2 || '',
        city: settingsData.addressOverride?.city || '',
        state: settingsData.addressOverride?.state || '',
        postalCode: settingsData.addressOverride?.postalCode || '',
        businessWebsiteLink: settingsData.businessWebsiteLink || '',
        businessWebsiteFaqLink: settingsData.businessWebsiteFaqLink || '',
      };

      const hoursData = settingsData.businessHours?.hours || DEFAULT_BUSINESS_HOURS;
      DAYS.forEach((day) => {
        const dayHours = hoursData[day];
        newFormValues[`${day}-checkbox`] = !!dayHours;
        newFormValues[`${day}-time-range`] = dayHours ? [dayHours.start ?? '', dayHours.end ?? ''] : ['', ''];
      });

      seedValues(newFormValues);
    }
    isInternalUpdate.current = false;
  }, [settingsData, seedValues]);

  // Sync form state changes back into the store draft (Internal -> External)
  useEffect(() => {
    if (Object.keys(values).length === 0) return;

    const updates: Partial<BusinessKnowledge> = {};
    let hasChanges = false;

    // 1. Business Name
    const nameSource = values.businessNameSource;
    const nameCustomVal = values.businessNameCustomValue;
    const currentName = settingsData.name;
    if (
      nameSource &&
      (nameSource !== currentName?.source || (nameSource === 'CUSTOM' && nameCustomVal !== currentName?.value))
    ) {
      updates.name = {
        source: nameSource,
        value: nameSource === 'CUSTOM' ? nameCustomVal : undefined,
      };
      hasChanges = true;
    }

    // 2. Business Website
    if (hasBusinessWebsiteField) {
      const websiteLink = values.businessWebsiteLink;
      if (websiteLink !== undefined && websiteLink !== settingsData.businessWebsiteLink) {
        updates.businessWebsiteLink = websiteLink;
        hasChanges = true;
      }
    }

    // 3. FAQs
    if (hasBusinessWebsiteFaqField) {
      const faqLink = values.businessWebsiteFaqLink;
      if (faqLink !== undefined && faqLink !== settingsData.businessWebsiteFaqLink) {
        updates.businessWebsiteFaqLink = faqLink;
        hasChanges = true;
      }
    }

    // 4. Business Address
    const addressSource = values.businessAddressSource;
    const currentAddress = settingsData.addressOverride;
    if (hasBusinessAddressField && addressSource) {
      const isCustom = addressSource === 'CUSTOM';
      const addressChanged =
        addressSource !== currentAddress?.source ||
        (isCustom &&
          (values.addressLine1 !== currentAddress?.addressLine1 ||
            values.addressLine2 !== currentAddress?.addressLine2 ||
            values.city !== currentAddress?.city ||
            values.state !== currentAddress?.state ||
            values.postalCode !== currentAddress?.postalCode));

      if (addressChanged) {
        updates.addressOverride = {
          source: addressSource,
          addressLine1: isCustom ? values.addressLine1 : undefined,
          addressLine2: isCustom ? values.addressLine2 : undefined,
          city: isCustom ? values.city : undefined,
          state: isCustom ? values.state : undefined,
          postalCode: isCustom ? values.postalCode : undefined,
        };
        hasChanges = true;
      }
    }

    // 5. Business Hours
    const hoursData = settingsData.businessHours?.hours || DEFAULT_BUSINESS_HOURS;
    let hoursChanged = false;
    const newHours: BusinessHoursMap = {};
    DAYS.forEach((day) => {
      const isChecked = getHoursCheckboxValue(values, day);
      const timeRange = getHoursTimeRangeValue(values, day);

      if (isChecked) {
        newHours[day] = { start: timeRange[0], end: timeRange[1] };
      }

      const oldHasHours = !!hoursData[day];
      if (isChecked !== oldHasHours) {
        hoursChanged = true;
      }
      if (isChecked && (timeRange[0] !== hoursData[day]?.start || timeRange[1] !== hoursData[day]?.end)) {
        hoursChanged = true;
      }
    });

    if (hoursChanged) {
      updates.businessHours = {
        source: 'CUSTOM',
        hours: newHours,
      };
      hasChanges = true;
    }

    if (hasChanges) {
      isInternalUpdate.current = true;
      updateDraft(locationId, updates);
    }
  }, [
    values,
    locationId,
    updateDraft,
    settingsData,
    hasBusinessAddressField,
    hasBusinessWebsiteField,
    hasBusinessWebsiteFaqField,
  ]);

  // Centralized Error Syncing
  useEffect(() => {
    // 1. Business Name Error
    const isSourceCustom = values.businessNameSource === 'CUSTOM';

    let nameHasError = false;
    if (isSourceCustom) {
      nameHasError = !!getFieldProps('businessNameCustomValue').error;
    } else {
      nameHasError = !!getFieldProps('businessNameSource').error;
    }

    if (!!fieldErrors[locationId]?.name !== nameHasError) {
      setFieldError(locationId, 'name', nameHasError);
    }

    // 2. Business Hours Error
    let hoursHasError = false;
    const isAnyCheckboxChecked = DAYS.some((day) => getHoursCheckboxValue(values, day));
    if (isAnyCheckboxChecked) {
      DAYS.forEach((day) => {
        if (getHoursCheckboxValue(values, day)) {
          const timeRange = getHoursTimeRangeValue(values, day);
          if (!timeRange || !timeRange[0] || !timeRange[1]) {
            hoursHasError = true;
          }
        }
      });
    } else if (!isManageAll) {
      hoursHasError = true;
    }
    if (!!fieldErrors[locationId]?.businessHours !== hoursHasError) {
      setFieldError(locationId, 'businessHours', hoursHasError);
    }

    // 3. Business Address Error
    let addressHasError = false;
    if (hasBusinessAddressField) {
      if (values.businessAddressSource === 'CUSTOM') {
        addressHasError = !!(
          getFieldProps('addressLine1').error ||
          getFieldProps('city').error ||
          getFieldProps('state').error ||
          getFieldProps('postalCode').error
        );
      } else {
        addressHasError = !!getFieldProps('businessAddressSource').error;
      }
    }
    if (!!fieldErrors[locationId]?.addressOverride !== addressHasError) {
      setFieldError(locationId, 'addressOverride', addressHasError);
    }

    // 4. Website Error
    const websiteHasError = hasBusinessWebsiteField ? !!getFieldProps('businessWebsiteLink').error : false;
    if (!!fieldErrors[locationId]?.businessWebsiteLink !== websiteHasError) {
      setFieldError(locationId, 'businessWebsiteLink', websiteHasError);
    }

    // 5. FAQ Error
    const faqHasError = hasBusinessWebsiteFaqField ? !!getFieldProps('businessWebsiteFaqLink').error : false;
    if (!!fieldErrors[locationId]?.businessWebsiteFaqLink !== faqHasError) {
      setFieldError(locationId, 'businessWebsiteFaqLink', faqHasError);
    }
  }, [
    values,
    locationId,
    setFieldError,
    fieldErrors,
    hasBusinessAddressField,
    hasBusinessWebsiteField,
    hasBusinessWebsiteFaqField,
    isManageAll,
    getFieldProps,
  ]);

  return {
    getFieldProps,
    values,
    settingsData,
    fieldErrors,
    hasBusinessAddressField,
    hasBusinessWebsiteField,
    hasBusinessWebsiteFaqField,
  };
};
